import { useEffect, useState, type ReactNode } from 'react'
import { Head, Link, usePage } from '@inertiajs/react'
import MarketingHeader from '@/components/MarketingHeader'
import { loadPaddle, openCheckout } from '@/lib/paddle'
import { money } from '@/lib/money'
import { t } from '@/lib/i18n'
import type { PaddleGlobal, PaddleProps, PricingProps, SharedProps } from '@/types'

function Feature({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-2 text-[15px] leading-relaxed">
      <span className="text-brand shrink-0">✓</span>
      <span>{children}</span>
    </li>
  )
}

export default function Show({ paddle, pricing, entitlement }: {
  paddle: PaddleProps
  pricing: PricingProps
  entitlement: 'free' | 'family'
}) {
  const currentUser = usePage<SharedProps>().props.current_user
  const [sdk, setSdk] = useState<PaddleGlobal | null>(null)
  const [failed, setFailed] = useState(false)

  // 只有真的能买才加载脚本：不可购买时（production 的 price id 还是 nil）
  // 连请求都不该发出去。
  useEffect(() => {
    if (!paddle.purchasable || !currentUser) return
    loadPaddle(paddle).then(setSdk).catch((e) => {
      console.error(e)
      setFailed(true)
    })
  }, [ paddle, currentUser ])

  const buy = (cycle: 'monthly' | 'yearly') => {
    const priceId = paddle.prices[cycle]
    if (!sdk || !priceId || !currentUser) return
    openCheckout(sdk, { priceId, email: currentUser.email_address, userId: currentUser.id })
  }

  return (
    <div className="font-sans">
      <Head title={t('pricing.head_title')} />
      <MarketingHeader />

      {/* 沙盒横幅。名单内的用户在生产站点上走的是假钱通道，必须当场看得见——
          否则连你自己都会分不清哪一笔是真的。 */}
      {paddle.environment === 'sandbox' && (
        <div className="bg-amber-tint border-b border-amber-line text-amber-strong text-[14px] text-center px-5 py-2.5 font-semibold">
          🧪 {t('pricing.sandbox_notice')}
        </div>
      )}

      <section className="max-w-[1080px] mx-auto px-5 pt-14 pb-8 text-center">
        <h1 className="text-[34px] md:text-[42px] font-extrabold tracking-tight leading-tight">
          {t('pricing.title')}
        </h1>
        <p className="mt-4 text-[17px] text-muted max-w-[620px] mx-auto leading-relaxed">
          {t('pricing.subtitle')}
        </p>
      </section>

      <section className="max-w-[900px] mx-auto px-5 pb-16 grid md:grid-cols-2 gap-5 items-start">
        {/* ---- 免费 ---- */}
        <div className="bg-card rounded-3xl border border-line card-shadow p-6 grid gap-4">
          <div>
            <div className="font-extrabold text-[20px]">{t('pricing.free.name')}</div>
            <div className="text-[14px] text-muted mt-1">{t('pricing.free.tagline')}</div>
          </div>
          <div className="text-[38px] font-extrabold">{t('pricing.free.price')}</div>
          <ul className="grid gap-2.5">
            <Feature>{t('pricing.free.feature_1')}</Feature>
            <Feature>{t('pricing.free.feature_2')}</Feature>
            <Feature>{t('pricing.free.feature_3')}</Feature>
            <Feature>{t('pricing.free.feature_4')}</Feature>
          </ul>
          {entitlement === 'free' && (
            <div className="text-[13px] font-semibold text-muted border-t border-line pt-3">
              {t('pricing.current_plan')}
            </div>
          )}
        </div>

        {/* ---- 家庭版 ---- */}
        <div className="bg-card rounded-3xl border-2 border-brand card-shadow p-6 grid gap-4">
          <div>
            <div className="font-extrabold text-[20px] grad-text">{t('pricing.family.name')}</div>
            <div className="text-[14px] text-muted mt-1">{t('pricing.family.tagline')}</div>
          </div>

          <div>
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-[38px] font-extrabold">{money(pricing.yearly_cents, pricing.currency)}</span>
              <span className="text-[16px] text-muted font-semibold">{t('pricing.per_year')}</span>
              <span className="ml-1 text-[12px] font-bold text-brand bg-tint px-2 py-1 rounded-full">
                {t('pricing.save_percent', { percent: pricing.yearly_save_percent })}
              </span>
            </div>
            <div className="text-[13px] text-muted mt-1.5">
              {t('pricing.yearly_equivalent', {
                price: money(pricing.yearly_monthly_equivalent_cents, pricing.currency),
              })}
              {' · '}
              {money(pricing.monthly_cents, pricing.currency)}{t('pricing.per_month')}
            </div>
          </div>

          <ul className="grid gap-2.5">
            <Feature>{t('pricing.family.feature_1')}</Feature>
            <Feature>{t('pricing.family.feature_2')}</Feature>
            <Feature>{t('pricing.family.feature_3')}</Feature>
            <Feature>{t('pricing.family.feature_4')}</Feature>
            <Feature>{t('pricing.family.feature_5')}</Feature>
          </ul>

          <div className="grid gap-2 border-t border-line pt-4">
            {entitlement === 'family' ? (
              <div className="text-[14px] font-bold text-brand">{t('pricing.current_plan')}</div>
            ) : !currentUser ? (
              // 结账要把订阅绑回一个 user（custom_data.user_id），所以必须先有账号。
              // 注册后落点是看板而不是回这一页——已知的转化漏点，见 todo A③。
              <Link
                href="/registration/new"
                className="text-center text-white grad-bg font-bold px-5 py-3 rounded-xl hover:opacity-90"
              >
                {t('pricing.signup_first')}
              </Link>
            ) : !paddle.purchasable ? (
              <>
                <button
                  disabled
                  className="text-center bg-bg text-faint font-bold px-5 py-3 rounded-xl cursor-not-allowed"
                >
                  {t('pricing.coming_soon')}
                </button>
                <p className="text-[13px] text-muted">{t('pricing.coming_soon_hint')}</p>
              </>
            ) : (
              <>
                <button
                  onClick={() => buy('yearly')}
                  disabled={!sdk}
                  className="text-center text-white grad-bg font-bold px-5 py-3 rounded-xl hover:opacity-90 disabled:opacity-50"
                >
                  {t('pricing.choose_yearly')}
                </button>
                <button
                  onClick={() => buy('monthly')}
                  disabled={!sdk}
                  className="text-center bg-card border border-line font-bold px-5 py-3 rounded-xl hover:border-brand disabled:opacity-50"
                >
                  {t('pricing.choose_monthly')}
                </button>
                {failed && (
                  <p className="text-[13px] text-red-strong">{t('pricing.script_failed')}</p>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-[620px] mx-auto px-5 pb-20 text-center text-[14px] text-muted">
        {t('pricing.refund_note')}
        <a href="/refund" className="text-brand font-semibold ml-1.5 hover:underline">
          {t('pricing.refund_link')}
        </a>
      </section>
    </div>
  )
}
